import { useEffect, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { PersistenceController, type SaveStatus } from './persist';
import { onState, readState } from './adapter';

const IDLE: SaveStatus = {
  state: 'idle',
  target: 'local',
  detail: 'Not connected to a project.',
  lastSavedAt: null,
  failures: 0,
};

export function usePersistence(enabled: boolean): {
  status: SaveStatus;
  saveNow: () => Promise<void>;
} {
  const [status, setStatus] = useState<SaveStatus>(IDLE);
  const controllerRef = useRef<PersistenceController | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new PersistenceController(() => store.wb, setStatus);
    controllerRef.current = controller;
    controller.start();

    let disposed = false;

    const bindAndHydrate = async (): Promise<void> => {
      controller.bind(readState('selectedProjectApp'), readState('projectAppsByAppID'));
      // What the user had open when this started. Hydration paints the local
      // copy, then fetches the project's — seconds later, over the network — so
      // an example opened in between would otherwise be replaced by the
      // workbook it was meant to replace.
      const epoch = store.userLoads;
      await controller.hydrate((wb) => {
        if (disposed || store.userLoads !== epoch) return;
        store.loadWorkbook(wb, 'restore');
      });
    };

    // The bridge answers before the handshake completes, so re-bind on the app
    // keys and retry hydration until one of them carries something. A retry is
    // free once hydration has concluded — `hydrate` returns on its latch — and
    // until then it is the ONLY thing that opens the write gate, so the ladder
    // runs long enough to outlast a slow handshake. (`project` is listened for
    // as well, but a key already set before we subscribed never broadcasts.)
    const off = onState(['selectedProjectApp', 'projectAppsByAppID', 'project'], () => {
      void bindAndHydrate();
    });
    void bindAndHydrate();
    const retries = [400, 1200, 2500, 5000, 9000, 15000].map((ms) =>
      setTimeout(() => void bindAndHydrate(), ms)
    );

    // A change the PROJECT caused, or a click that only moved the cursor, is
    // still saved — it just is not announced as the user's unsaved work. See
    // markDirty, and store.emitUiOnly.
    const unsubscribe = store.subscribe(() => controller.markDirty(!store.lastEmitWasIncidental));

    return () => {
      disposed = true;
      off();
      for (const t of retries) clearTimeout(t);
      unsubscribe();
      controller.stop();
      controllerRef.current = null;
    };
  }, [enabled]);

  /*
   * The exit flush must NOT depend on a callback that changes identity per
   * edit: a `useCallback` over the document is a new function every keystroke,
   * so the effect re-runs and its CLEANUP — the save meant for unmount — fires
   * on every edit, straight past the debounce beside it. The controller lives
   * in a ref and this effect has an empty dep list, so the cleanup runs exactly
   * once, on unmount.
   *
   * `blur` and `visibilitychange`-visible are deliberately absent: a
   * cross-origin iframe is torn down before an async rpc finishes, so the exit
   * hooks are pagehide, hidden, and Giraffe's own closing signal.
   */
  useEffect(() => {
    const flush = (): void => {
      void controllerRef.current?.flush(true);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    const off = onState(['closingSignal'], flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      off();
      flush();
    };
  }, []);

  return {
    status,
    saveNow: async () => {
      // Pressing Save when the project copy could not be read means "try
      // again", not "overwrite it" — the read has to succeed before anything
      // goes out, and retryRead saves once it does.
      const controller = controllerRef.current;
      if (!controller) return;
      // Before the restore has landed, Save cannot mean "send this" — there is
      // nothing on screen yet that is known to be the project's work. It means
      // "finish opening", which is the read that was still outstanding.
      if (!controller.isRestored()) {
        await controller.retryHydrate();
        return;
      }
      if (controller.isReadBlocked()) {
        await controller.retryRead();
        return;
      }
      await controller.flush(true);
    },
  };
}
